/**
 * AuthTokenSync
 *
 * Invisible component that syncs the OIDC access token to the module-level
 * auth store so non-React code (GraphQL client) can access it.
 *
 * Also invalidates React Query caches when the token changes, so that
 * auth-dependent queries (like visibleApplicationScopes) re-fetch with
 * the new credentials.
 */

import { useEffect, useRef } from 'react';
import { useAuth } from 'react-oidc-context';
import { useQueryClient } from '@tanstack/react-query';
import { setAccessToken, clearAccessToken } from '@/lib/auth';
import { resetWSClient } from '@/lib/graphql/client';

export function AuthTokenSync() {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const previousTokenRef = useRef<string | null>(null);

  useEffect(() => {
    // Use the access_token from the user object, but only if it hasn't expired.
    // Sending an expired token causes a 401 → auth:unauthorized → renewal loop.
    // If the token is expired, let the OIDC library renew it first; we'll pick
    // up the fresh token when auth.user updates.
    const user = auth.user;
    const newToken = user?.access_token ?? null;
    const isExpired = user?.expired ?? false;

    if (newToken && !isExpired) {
      setAccessToken(newToken, user?.expires_at);
    } else if (!user) {
      // Truly logged out — clear the token
      clearAccessToken();
    } else if (isExpired) {
      // Token expired — clear so we don't send stale credentials.
      // The OIDC library's automaticSilentRenew will provide a fresh token.
      clearAccessToken();
    }

    // When the token actually changes, reset connections and invalidate
    // auth-dependent query caches so they re-fetch with new credentials.
    if (newToken && previousTokenRef.current !== newToken) {
      resetWSClient();
      // Invalidate scope cache — scope visibility depends on the principal
      // resolved from the token, so stale cache from a previous token is wrong.
      queryClient.invalidateQueries({ queryKey: ['applicationScopes'] });
      previousTokenRef.current = newToken;
    }
  }, [auth.isAuthenticated, auth.user, auth.user?.access_token, queryClient]);

  // This component doesn't render anything
  return null;
}

export default AuthTokenSync;
